import React, { MouseEvent, ReactNode, useState } from 'react';
import './Button.scss';

/**
 * Button: To show button component
 *
 * Example :
 *
 * <Button
      buttonClass="button-space" // we can add custom class on button
      variant={primaryButtonVariant} // any of the variant from below variants
      onClick={primaryButtonOnClick} // function which can be call on this button click
    >
      {primaryButtonText}
    </Button>
 *  variants:- "primary"| "secondary"| "danger"| "disabled"| "large"| "medium"| "small"| "dropdown";
 */

interface Props {
    variant:
        | 'primary'
        | 'secondary'
        | 'danger'
        | 'dark'
        | 'disabled'
        | 'large'
        | 'medium'
        | 'small'
        | 'success'
        | 'dropdown'
        | 'link'
        | 'new-primary'
        | 'new-secondary';
    onClick?: (e: MouseEvent) => void;
    buttonClass?: string;
    disabled?: boolean;
    isAllowDisable?: boolean;
    isUsePropagation?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    displayType?: 'default' | 'block' | 'icon-only';
    ariaLabel?: string;
    style?: React.CSSProperties;
}

export const Button: React.FC<Partial<Props>> = (props) => {
    const {
        variant = 'primary',
        disabled = false,
        buttonClass,
        onClick,
        children,
        isAllowDisable = false,
        isUsePropagation = true,
        startIcon,
        endIcon,
        displayType = 'default',
        ariaLabel,
        style,
        ...rest
    } = props;

    const [isDisable, setIsDisable] = useState(false);
    const hasIcon = Boolean(startIcon || endIcon);
    const computedClassName = `button ${variant} ${
        displayType === 'block' ? 'block' : ''
    } ${displayType === 'icon-only' ? 'icon-only' : ''} ${
        hasIcon ? 'with-icon' : ''
    } ${buttonClass ?? ''}`.trim();

    return (
        <button
            className={computedClassName}
            disabled={disabled || (isAllowDisable && isDisable)}
            type="button"
            aria-label={displayType === 'icon-only' ? ariaLabel : undefined}
            {...rest}
            style={style}
            onClick={(e) => {
                if (isUsePropagation) e.stopPropagation();
                if (onClick) {
                    if (isAllowDisable) {
                        setIsDisable(true);
                        setTimeout(() => {
                            setIsDisable(false);
                        }, 400);
                    }
                    onClick(e);
                }
            }}
        >
            <span className="button-content">
                {displayType === 'icon-only' ? (
                    <span className="button-icon center">
                        {startIcon || endIcon || children}
                    </span>
                ) : (
                    <>
                        {startIcon ? (
                            <span className="button-icon start">{startIcon}</span>
                        ) : null}

                        <div className="button-label">{children}</div>

                        {endIcon ? (
                            <span className="button-icon end">{endIcon}</span>
                        ) : null}
                    </>
                )}
            </span>
        </button>
    );
};
